import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTour, type TourStep } from '@shared/tour/Tour';
import { useAuth } from '@shared/auth/AuthContext';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { useLocale } from '@shared/i18n/useLocale';
import { IconDefs, Footer } from '@shared/ui';
import { NamedIcon } from '@shared/ui/icons';
import { SupportWidget } from '@shared/support/SupportWidget';
import { useUnreadThreads } from '@shared/hooks/useUnreadThreads';
import { useShortlist } from './shortlistStore';
import PreviewBanner from './PreviewBanner';
import './customer.css';

const BROWSE_FEATURES = ['an_social', 'an_digital', 'an_outdoor'];

export default function CustomerLayout() {
  const { t } = useTranslation(['customer', 'common']);
  const { user, signOut, hasFeature, role, accessStatus } = useAuth();
  const preview = role !== 'admin' && accessStatus !== 'active';
  const { count: shortlistCount } = useShortlist();
  const { total: unreadTotal } = useUnreadThreads();
  const { locale, setLocale, locales, label } = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);
  // Lift the support button above the phone bottom bar.
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const on = () => setIsPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const navCls = ({ isActive }: { isActive: boolean }) => (isActive ? 'active' : '');
  const showBrowse = BROWSE_FEATURES.some(hasFeature);
  const close = () => setMenuOpen(false);
  // «Рынок» covers both the new /market screens and the legacy /browse tabs.
  const { pathname } = useLocation();
  const onMarket = pathname.startsWith('/customer/market') || pathname.startsWith('/customer/browse');
  const bottomItem = (to: string, icon: string, text: string, forceActive?: boolean) => (
    <NavLink to={to} onClick={close} className={({ isActive }) => `bottom-nav-item${isActive || forceActive ? ' active' : ''}`}>
      <span className="bottom-nav-icon"><NamedIcon name={icon} size={20} /></span>
      <span>{text}</span>
    </NavLink>
  );

  // Guided intro tour. Steps are built to match the visible, feature-gated nav;
  // on mobile the engine opens the hamburger (reveal='.nav-toggle') to reach them.
  const tourSteps = useMemo<TourStep[]>(() => {
    const s: TourStep[] = [
      { title: t('customer:tour.welcome.title', { defaultValue: 'Добро пожаловать в AdaMar' }),
        body: t('customer:tour.welcome.body', { defaultValue: 'Короткий тур по разделам — 30 секунд.' }) },
    ];
    if (showBrowse) s.push({ target: '[data-tour="adv-browse"]', reveal: '.nav-toggle',
      title: t('customer:tour.browse.title', { defaultValue: 'Обзор рынка' }),
      body: t('customer:tour.browse.body', { defaultValue: 'Кто и где рекламируется — блогеры и конкуренты.' }) });
    if (hasFeature('act_request')) s.push({ target: '[data-tour="adv-campaigns"]', reveal: '.nav-toggle',
      title: t('customer:tour.campaigns.title', { defaultValue: 'Задания' }),
      body: t('customer:tour.campaigns.body', { defaultValue: 'Опубликуй бриф — блогеры откликнутся сами.' }) });
    if (hasFeature('an_my') || preview) s.push({ target: '[data-tour="adv-analytics"]', reveal: '.nav-toggle',
      title: t('customer:tour.analytics.title', { defaultValue: 'Моя аналитика' }),
      body: preview
        ? t('customer:tour.analyticsPreview.body', { defaultValue: 'Подключите свой бренд — и здесь появятся его упоминания, охваты и блогеры.' })
        : t('customer:tour.analytics.body', { defaultValue: 'Упоминания и охваты твоего бренда.' }) });
    if (hasFeature('an_price')) s.push({ target: '[data-tour="adv-price"]', reveal: '.nav-toggle',
      title: t('customer:tour.price.title', { defaultValue: 'Проверка цен' }),
      body: t('customer:tour.price.body', { defaultValue: 'Честная вилка цен и подбор блогеров.' }) });
    s.push({ target: '[data-tour="adv-profile"]', reveal: '.nav-toggle',
      title: t('customer:tour.profile.title', { defaultValue: 'Профиль' }),
      body: t('customer:tour.profile.body', { defaultValue: 'Компания, бренды и настройки аккаунта.' }) });
    s.push({ title: t('customer:tour.final.title', { defaultValue: 'Готово!' }),
      body: t('customer:tour.final.body', { defaultValue: 'Пройти тур снова можно из кнопки поддержки «?» внизу справа.' }) });
    return s;
  }, [t, showBrowse, hasFeature, preview]);
  const tour = useTour({
    id: `advertiser_${user?.id ?? 'x'}`,
    steps: tourSteps,
    // A real user id only: under the 'x' fallback the tour armed, then re-armed under the real key.
    enabled: !!user?.id,
    labels: {
      skip: t('customer:tour.skip', { defaultValue: 'Пропустить' }),
      back: t('customer:tour.back', { defaultValue: 'Назад' }),
      next: t('customer:tour.next', { defaultValue: 'Далее' }),
      done: t('customer:tour.done', { defaultValue: 'Погнали' }),
    },
  });

  return (
    <div className="customer-root glow-bg">
      {/* IconDefs renders the shared linearGradient that every bespoke icon
       * references via stroke="url(#kgdb-icon-grad)". Mount once at the
       * shell so all child icons inherit the theme blue→purple stroke. */}
      <IconDefs />
      {/* Subtle static gas-glow only — the animated atom was removed as it
       * distracted from the analytics content. */}
      <div className="glow-bg-mid" aria-hidden="true" />
      <a href="#main" className="skip-link">{t('common:nav.skipToContent', { defaultValue: 'Перейти к содержимому' })}</a>
      <header className="customer-head">
        <div className="brand">Ada<span>Mar</span></div>

        {/* Hamburger — mobile only (CSS-gated). Toggles the menu below. */}
        <button
          type="button"
          className={`nav-toggle${menuOpen ? ' open' : ''}`}
          aria-label={t('common:nav.menu', { defaultValue: 'Menu' })}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span /><span /><span />
        </button>

        {/* On desktop this is the inline row; on mobile it's the dropdown panel. */}
        <div className={`customer-menu${menuOpen ? ' open' : ''}`}>
          <nav className="customer-nav" onClick={close}>
            {hasFeature('an_social') && <NavLink to="creators" className={(a) => `${navCls(a)} nav-primary`}>{t('customer:nav.creators', { defaultValue: 'Блогеры' })}</NavLink>}
            {showBrowse && <NavLink to="market" className={() => `${onMarket ? 'active' : ''} nav-primary`} data-tour="adv-browse">{t('customer:nav.browse')}</NavLink>}
            {hasFeature('act_request') && (
              <NavLink to="campaigns" className={navCls} data-tour="adv-campaigns">
                {t('customer:nav.campaigns', { defaultValue: 'Задания' })}
                {unreadTotal > 0 && <span className="badge">{unreadTotal > 9 ? '9+' : unreadTotal}</span>}
              </NavLink>
            )}
            {hasFeature('an_my') && (
              <NavLink to="my-analytics" className={(a) => `${navCls(a)} nav-primary`} data-tour="adv-analytics">{t('customer:nav.myAnalytics', { defaultValue: 'My analytics' })}</NavLink>
            )}
            {preview && (
              <NavLink to="connect" className={(a) => `${navCls(a)} nav-primary`} data-tour="adv-analytics">
                {t('customer:nav.myAnalytics', { defaultValue: 'My analytics' })}
                <span className="badge">{t('customer:nav.connectBadge', { defaultValue: 'подключить' })}</span>
              </NavLink>
            )}
            {hasFeature('an_price') && (
              <NavLink to="price-check" className={navCls} data-tour="adv-price">
                {t('customer:nav.priceCheck', { defaultValue: 'Проверка цен' })}
                {shortlistCount > 0 && <span className="badge">{shortlistCount}</span>}
              </NavLink>
            )}
            <NavLink to="profile" className={navCls} data-tour="adv-profile">{t('customer:nav.profile')}</NavLink>
          </nav>

          {/* Language: a compact <select> on desktop, tappable pills in the mobile menu. */}
          <span className="lang-select"><LanguageSwitcher /></span>
          <div className="menu-lang">
            <span className="menu-lang-label">{t('common:nav.language', { defaultValue: 'Язык' })}</span>
            <div className="lang-pills">
              {locales.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`lang-pill${locale === l ? ' active' : ''}`}
                  onClick={() => setLocale(l)}
                >{label[l]}</button>
              ))}
            </div>
          </div>

          <span className="email">{user?.email}</span>
          <button className="signout" onClick={() => { close(); signOut(); }}>{t('common:nav.signOut')}</button>
        </div>
      </header>
      {menuOpen && <div className="menu-backdrop" onClick={close} aria-hidden="true" />}
      {/* Phone: the main sections sit under the thumb; "More" opens the rest of the menu. */}
      <nav className="bottom-nav" aria-label={t('common:nav.menu', { defaultValue: 'Menu' })}>
        {hasFeature('an_social') && bottomItem('creators', 'search', t('customer:nav.creators', { defaultValue: 'Блогеры' }))}
        {showBrowse && bottomItem('market', 'monitor', t('customer:nav.browse'), onMarket)}
        {hasFeature('an_my') && bottomItem('my-analytics', 'star', t('customer:nav.myAnalytics', { defaultValue: 'Мой бренд' }))}
        {preview && bottomItem('connect', 'star', t('customer:nav.myAnalytics', { defaultValue: 'Мой бренд' }))}
        <button type="button" className={`bottom-nav-item${menuOpen ? ' active' : ''}`} onClick={() => setMenuOpen((o) => !o)} aria-expanded={menuOpen}>
          <span className="bottom-nav-icon">
            <NamedIcon name="list" size={20} />
            {(unreadTotal + shortlistCount) > 0 && <span className="bottom-nav-badge">{(unreadTotal + shortlistCount) > 9 ? '9+' : unreadTotal + shortlistCount}</span>}
          </span>
          <span>{t('customer:nav.more', { defaultValue: 'Ещё' })}</span>
        </button>
      </nav>
      <main id="main" tabIndex={-1} className="customer-main">
        <PreviewBanner />
        <Outlet />
      </main>
      <Footer />
      <SupportWidget bottomOffset={isPhone ? '76px' : '18px'} onReplayTour={tour.start} />
      {tour.render}
    </div>
  );
}
